import {
  BarChart3,
  Building2,
  CheckSquare,
  Coins,
  Diamond,
  EllipsisVertical,
  FilePen,
  Inbox,
  LayoutDashboard,
  NewspaperIcon,
  Phone,
  Settings2,
} from 'lucide-react';
import { useDialog } from '@/code/frontend/components/hooks/use-dialog';
import { useCrmSurface } from '@/code/frontend/components/hooks/useCrmSurface';
import { usePostManagerAccess } from '@/code/frontend/components/posts/shared/postManagerAccess';
import { ReferralPage } from '@/code/frontend/components/ReferralPage';
import { Button } from '@/code/frontend/components/ui/button';
import { NavigationMenu } from '@/code/frontend/components/ui/navigation-menu/navigation-menu';
import { useOnboardingStatus } from '@/code/frontend/hooks/useOnboardingStatus';
import { LinkRouter } from '@/code/lib/routes';
import { trpc } from '@/code/lib/utils/trpc';

interface OrganizationNavigationProps {
  organizationSlug: string;
}

export const OrganizationNavigation: React.FC<OrganizationNavigationProps> = ({
  organizationSlug,
}) => {
  const { hasCompletedSetup, isLoading } = useOnboardingStatus();
  const utils = trpc.useUtils();
  const referralDialog = useDialog(ReferralPage);

  const userCanSendReferrals =
    trpc.featureFlags.getIsFlagEnabledForUser.useQuery('CAN_SEND_REFERRALS');
  const postManagerAccess = usePostManagerAccess();
  const userCanViewCallsCalendar =
    trpc.featureFlags.getIsFlagEnabledForUser.useQuery(
      'CAN_VIEW_CALLS_CALENDAR',
    );
  const isAdmin = trpc.organizations.isUserAdminOfOrganization.useQuery();
  // UI gate: CRM nav renders only when crmEnabled AND SHOULD_RENDER_CRM_FEATURES.
  const crm = useCrmSurface();
  const canViewCompanyLists =
    trpc.featureFlags.getIsFlagEnabledForUser.useQuery(
      'CAN_VIEW_COMPANY_LISTS',
    );
  const canViewMessagingInbox =
    trpc.featureFlags.getIsFlagEnabledForUser.useQuery(
      'SHOULD_SHOW_ORG_MESSAGING_INBOX',
    );
  const pendingTaskCount = trpc.tasks.getPendingCount.useQuery();

  const shouldRenderButtons = !isLoading && hasCompletedSetup;

  if (!shouldRenderButtons) {
    return null;
  }

  const prefetchTeamDashboard = () => {
    void utils.organizationNotification.getNotifications.prefetch();
    void utils.organizations.isUserAdminOfOrganization.prefetch();
    void utils.organizations.getTeamMembers.prefetch();
  };

  const prefetchPersonalDashboard = () => {
    void utils.user.get.prefetch({ noNestedObjects: true });
    void utils.campaign.getLeadUpdates.prefetch();
    void utils.leads.getDashboardSummary.prefetch({});
    void utils.campaign.getAll.prefetch({
      includeAll: true,
      currentUserOnly: true,
    });
  };

  const hasOtherItems =
    (userCanViewCallsCalendar.isFetched && userCanViewCallsCalendar.data) ||
    (isAdmin.isFetched && isAdmin.data && crm.canConfigure) ||
    (isAdmin.isFetched && isAdmin.data && canViewCompanyLists.data);

  return (
    <div className="flex items-center gap-2">
      <NavigationMenu>
        <NavigationMenu.List>
          {/* Top-level: New Campaign */}
          <NavigationMenu.Item>
            <NavigationMenu.NavLink
              href={LinkRouter.routeForOrganizationNewCampaign(
                organizationSlug,
              )}
              iconLeft={<FilePen />}
            >
              New Campaign
            </NavigationMenu.NavLink>
          </NavigationMenu.Item>

          {/* Top-level: Team Dashboard */}
          <NavigationMenu.Item>
            <NavigationMenu.NavLink
              href={LinkRouter.routeForOrganizationHome(organizationSlug)}
              onMouseEnter={prefetchTeamDashboard}
              iconLeft={<LayoutDashboard />}
            >
              Team Overview
            </NavigationMenu.NavLink>
          </NavigationMenu.Item>

          {/* Top-level: Personal Dashboard */}
          <NavigationMenu.Item>
            <NavigationMenu.NavLink
              href={LinkRouter.routeForOrganizationPersonalPage(
                organizationSlug,
              )}
              onMouseEnter={prefetchPersonalDashboard}
              iconLeft={<Diamond />}
            >
              Personal Dashboard
            </NavigationMenu.NavLink>
          </NavigationMenu.Item>

          {/* Top-level: Analytics */}
          <NavigationMenu.Item>
            <NavigationMenu.NavLink
              href={LinkRouter.routeForAnalytics(organizationSlug)}
              iconLeft={<BarChart3 />}
            >
              Analytics
            </NavigationMenu.NavLink>
          </NavigationMenu.Item>

          {/* Top-level: Inbox (messaging approval queue) — flag-gated, not
              admin-gated (matches the page gate). */}
          {canViewMessagingInbox.isFetched && canViewMessagingInbox.data && (
            <NavigationMenu.Item>
              <NavigationMenu.NavLink
                href={LinkRouter.routeForOrganizationInbox(organizationSlug)}
                iconLeft={<Inbox />}
              >
                Inbox
              </NavigationMenu.NavLink>
            </NavigationMenu.Item>
          )}

          {/* Top-level: Tasks */}
          <NavigationMenu.Item>
            <NavigationMenu.NavLink
              href={LinkRouter.routeForTasks()}
              iconLeft={<CheckSquare />}
              badge={
                (pendingTaskCount.data ?? 0) > 0
                  ? pendingTaskCount.data
                  : undefined
              }
            >
              Tasks
            </NavigationMenu.NavLink>
          </NavigationMenu.Item>

          {/* Reps used to live here as a separate top-nav entry. It now
              renders as a tab on the home/Team Overview page so the funnel
              metrics flow into the rep roster naturally. */}

          {/* Top-level: Post Manager (conditional) */}
          {postManagerAccess.isFetched && postManagerAccess.showPostManager && (
            <NavigationMenu.Item>
              <NavigationMenu.NavLink
                href={LinkRouter.routeForOrganizationPostManager(
                  organizationSlug,
                )}
                iconLeft={<NewspaperIcon />}
              >
                Post Manager
              </NavigationMenu.NavLink>
            </NavigationMenu.Item>
          )}

          {/* "Other" dropdown for less-used items */}
          {hasOtherItems && (
            <NavigationMenu.Item>
              <NavigationMenu.Trigger iconLeft={<EllipsisVertical />}>
                Other
              </NavigationMenu.Trigger>
              <NavigationMenu.Content>
                {userCanViewCallsCalendar.isFetched &&
                  userCanViewCallsCalendar.data && (
                    <NavigationMenu.DropdownLink
                      href={LinkRouter.routeForOrganizationCallsCalendar(
                        organizationSlug,
                      )}
                      iconLeft={<Phone />}
                    >
                      Calls Calendar
                    </NavigationMenu.DropdownLink>
                  )}

                {isAdmin.isFetched && isAdmin.data && crm.canConfigure && (
                  <NavigationMenu.DropdownLink
                    href={LinkRouter.routeForCrmSettings(organizationSlug)}
                    iconLeft={<Settings2 />}
                  >
                    CRM Settings
                  </NavigationMenu.DropdownLink>
                )}

                {isAdmin.isFetched &&
                  isAdmin.data &&
                  canViewCompanyLists.data && (
                    <NavigationMenu.DropdownLink
                      href={LinkRouter.routeForCompanyLists(organizationSlug)}
                      iconLeft={<Building2 />}
                    >
                      Company Lists
                    </NavigationMenu.DropdownLink>
                  )}
              </NavigationMenu.Content>
            </NavigationMenu.Item>
          )}
        </NavigationMenu.List>
      </NavigationMenu>

      {/* Refer & Earn - standalone CTA button outside NavigationMenu */}
      {userCanSendReferrals.isFetched && userCanSendReferrals.data && (
        <Button
          variant="airfoil-primary"
          size="airfoil"
          iconLeft={<Coins />}
          onClick={() =>
            referralDialog.open(
              {},
              {
                title: null,
                width: 'screen-xl',
                hideCloseButton: true,
                classNames: {
                  content: 'p-0',
                },
              },
            )
          }
        >
          Refer & Earn
        </Button>
      )}
    </div>
  );
};

export default OrganizationNavigation;
